import * as React from 'react';
import styled from 'styled-components';
import Colours from '../../State/Colours';
import Context from '../../State/Context';
import devices from '../../State/Devices';
import { EPage, ESourceType } from '../../Types/Model';
import Button from '../Button';
import { ControlOther } from '../ControlOther';
import { ControlStock } from '../ControlStock';
import Markdown from '../Markdown';
import { Input } from '../MlrForm';
import { Select, TextToSelectOption } from '../Select';
import Switch from '../Switch';
import Tooltip from '../Tooltip';
import Total from '../Total';
import TotalFooter from '../TotalFooter';

interface IControlData {
    label: string
    inputType?: 'text' | 'password' | 'number'
    options?: string[]
    tooltip?: string
    onChange?: React.FormEventHandler
    value: number|string
    step?: number
    max?: number
}

interface IValueInputComponent extends IControlData {
    className?: string
    disabled?: boolean
}

const Label = styled.strong`
    display: block;
    font-size: 12px;
`;

const ValueInputStyled = styled.div`
    display: -ms-grid;
    display: grid;
    grid-template-columns: auto 1fr;
    -ms-grid-columns: 24px 1fr; /* auto is not the same in IE */
    grid-template-areas: "tooltip itemlabel" /* TODO */
                         "userInput userInput";

    span { grid-area: tooltip; color: ${Colours.gray}; }
    strong { grid-area: itemlabel; align-self: center; }
    select, input { grid-area: userInput; }

    & *:nth-child(1) { -ms-grid-column: 1; -ms-grid-row: 1; }
    & *:nth-child(2) { -ms-grid-column: 2; -ms-grid-row: 1; }
    & *:nth-child(3) { -ms-grid-column: 2; -ms-grid-row: 2; }

    input {
        text-align: right;
    }
`;

const ValueInput = ({ label, inputType, options, tooltip, onChange, value, disabled, step, max }: IValueInputComponent) => {
    return (
        <ValueInputStyled>
            {tooltip && tooltip.length > 0 && <Tooltip text={tooltip} position="left"/>}
            <Label style={{ textAlign: 'center' }}>{label}</Label>
            {options && (
                <Select 
                    options={options.map(TextToSelectOption)} 
                    defaultOption={TextToSelectOption('Please select')}
                    onChange={onChange}
                    selectedValue={value}
                    disabled={disabled}
                />
            )}
            {inputType && (<Input type={inputType} onChange={onChange} value={value} disabled={disabled} step={step} max={max}/>)}
        </ValueInputStyled>
    );
};

const ItemLabel = styled.span`
    justify-self: start;
    align-self: center;
    margin-block: 0;
    font-size: 24px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 0.2px;

    -ms-grid-column-align: start;
    -ms-grid-row-align: center;
`;

const CenteredColumn = styled.div`
    align-self: center;
    display: -ms-grid;
    display: grid;

    -ms-grid-row-align: center;
`;

const RowStyled = styled.div`
    display: grid;
    min-height: 60px;
    grid-template-columns: 60px 300px 1fr 100px;
    grid-column-gap: 10px; /* TODO */
    grid-row-gap: 4px; /* TODO */

    display: -ms-grid;
    -ms-grid-columns: 60px 300px 1fr 100px;
    & *:nth-child(1) { -ms-grid-column: 1; }
    & *:nth-child(2) { -ms-grid-column: 2; }
    & *:nth-child(3) { -ms-grid-column: 3; }
    & *:nth-child(4) { -ms-grid-column: 4; }
    
    @media ${devices.tablet} {
        grid-template-columns: 1fr 90px;
        -ms-grid-columns: 1fr 90px;
        grid-template-areas: "RowTitle RowSwitch" /* TODO */
                             "RowInputs RowTotal";
    }
`;

const SwitchColumnStyle = styled(CenteredColumn)`
    @media ${devices.tablet} { grid-area: RowSwitch; justify-self: end; }
`;
const ItemTitleColumnStyle = styled(CenteredColumn)`
    @media ${devices.tablet} { grid-area: RowTitle; }
`;
const InputsColumnStyle = styled(CenteredColumn)`
    justify-content: end;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 4px; /* TODO */
    
    -ms-grid-columns: 1fr 1fr;
    & *:nth-child(1) { -ms-grid-column: 1; }
    & *:nth-child(2) { -ms-grid-column: 3; }

    @media ${devices.tablet} {
        grid-area: RowInputs;
        grid-template-columns: 1fr;
        -ms-grid-columns: 1fr;
    }
`;

const RowContainer = styled.div`
    hr.hr {
        margin: 8px 0;
    }

    @media ${devices.tablet} {
        hr.hr {
            margin: 20px 0;
        }
    }
`;

const StockRowStyled = styled.div`
    display: block;
`;

const OtherRow = styled.div`
    display: block;
`;

const ToggleRow = styled.div`
    display: -ms-grid;
    display: grid;
    grid-template-columns: 60px 1fr;
    -ms-grid-columns: 60px 1fr;
    grid-column-gap: 10px; /* TODO */
    align-items: center;

    & *:nth-child(1) { -ms-grid-column: 1; }
    & *:nth-child(2) { -ms-grid-column: 2; }

    @media ${devices.tablet} {
        grid-template-areas: "ItemLabel Switch"; /* TODO */
        grid-template-columns: 1fr 1fr;
        -ms-grid-columns: 1fr 1fr;

        .ItemLabel {
            grid-area: ItemLabel;
        }

        .Switch {
            grid-area: Switch;
            justify-self: end;
            -ms-grid-column-align: end;
        }
    }
`;

const LayoutStock = styled.div`
    display: grid;
    grid-template-columns: 1fr 100px 100px;
    grid-column-gap: 10px; /* TODO */
    grid-row-gap: 4px;
    margin: 8px 0px;

    display: -ms-grid;
    -ms-grid-columns: 1fr 100px 100px;
    & *:nth-child(3n+1) { -ms-grid-column: 1; }
    & *:nth-child(3n+2) { -ms-grid-column: 2; }
    & *:nth-child(3n) { -ms-grid-column: 3; }

    @media ${devices.tablet} {
        grid-template-columns: 1fr 50px 80px;
        -ms-grid-columns: 1fr 50px 80px;
    }
`;

const LayoutOther = styled.div`
    display: grid;
    grid-template-columns: 1fr 216px;
    grid-column-gap: 10px; /* TODO */
    grid-row-gap: 4px; /* TODO */
    margin: 8px 0px;

    -ms-grid-columns: 1fr 216px;
    display: -ms-grid;            
    & *:nth-child(1n) { -ms-grid-column: 1; }
    & *:nth-child(2n) { -ms-grid-column: 2; }

    @media ${devices.tablet} {
        grid-template-columns: 1fr 80px;
        -ms-grid-columns: 1fr 80px;
    }
`;

const TotalWithGridArea = styled(Total)`
    @media ${devices.tablet} {
        grid-area: RowTotal;
    }
`;

interface ICustomRowComponent {
    label: string
    total?: string
    controls?: IControlData[]
    type: ESourceType
    placeholder?: string
}

const GenericRowComponent = ({ type, label, controls, total }: ICustomRowComponent) => (
    <Context.Consumer>
        {({ onToggleOption, userState }) => {
            const enabled = userState.enabledOptions.indexOf(type) > -1;
            const onToggle = () => onToggleOption(type, !enabled);

            return (
                <React.Fragment>
                    <RowStyled>
                        <SwitchColumnStyle>
                            <Switch checked={enabled === true} onChange={onToggle} value="checked" color="primary"/>
                        </SwitchColumnStyle>

                        <ItemTitleColumnStyle>
                            <ItemLabel>{label}</ItemLabel>
                        </ItemTitleColumnStyle>
                        
                        <InputsColumnStyle>
                            {controls && controls.length === 1 && <div/>}
                            {controls && controls.map((item, index) => (
                                <ValueInput 
                                    key={index} {...item}
                                    onChange={item.onChange} 
                                    disabled={!enabled}
                                    step={item.step}
                                    max={item.max}
                                />
                            ))}
                        </InputsColumnStyle>
                        
                        {total && (<TotalWithGridArea enabled={enabled}>{total}</TotalWithGridArea>)}
                    </RowStyled>
                    <hr className="hr gray"/>
                </React.Fragment>
            );
        }}
    </Context.Consumer>
);

const StockRowComponent = ({ type, label }: ICustomRowComponent) => (
    <Context.Consumer>
        {({ stock, messages, onToggleOption, userState, onAddStock, onChangeStockName, onChangeStockValue }) => {
            const enabled = userState.enabledOptions.indexOf(type) > -1;
            const onToggle = () => onToggleOption(type, !enabled);
            const {
                labelTypeOfStock,
                labelQuantity,
                buttonAddAnotherStock,
                tooltipStock,
            } = messages.page2;

            const { stockSelections } = userState;

            const stockOptions = stock.map(item => ({
                id: item.name,
                label: item.name,
                value: item.name,
            }));

            return (
                <React.Fragment>
                    <StockRowStyled>
                        <ToggleRow>
                            <Switch className="Switch" checked={enabled === true} onChange={onToggle} value="checked" color="primary"/>
                            <ItemLabel className="ItemLabel">{label} {tooltipStock && tooltipStock.length > 0 && <Tooltip text={tooltipStock} position="left"/>}</ItemLabel>
                        </ToggleRow>

                        <LayoutStock>
                            {stockSelections.length > 0 && (
                                <React.Fragment>
                                    <Label>{labelTypeOfStock}</Label>
                                    <Label>{labelQuantity}</Label>
                                    <Label>&nbsp;</Label>
                                </React.Fragment>
                            )}
                            {stockSelections.map((item, index) => {
                                const onChangeName = (value: string) => onChangeStockName(index, value);
                                const onChangeValue = (value: number) => onChangeStockValue(index, value);
                                return (
                                    <ControlStock
                                        rowNumber={index + 2}
                                        isParentEnabled={enabled} 
                                        key={index} 
                                        name={item.name}
                                        value={Number(item.value)}
                                        total={item.total} 
                                        onChangeName={onChangeName}
                                        onChangeValue={onChangeValue}
                                    />
                                );
                            })}
                        </LayoutStock>                        
                    </StockRowStyled>
                    <Select
                        disabled={!enabled}
                        defaultOption={{ id: 'addAnother', label: buttonAddAnotherStock, value: 'addAnother' }}
                        options={stockOptions}
                        selectedValue="addAnother"
                        style={{ width: '100%' }}
                        onChange={onAddStock}
                    />
                    <hr className="hr gray"/>
                </React.Fragment>
            )
        }}
    </Context.Consumer>
);

const OtherRowComponent = ({ type, label, placeholder }: ICustomRowComponent) => (
    <Context.Consumer>
        {({ messages, onToggleOption, userState, onAddOtherUnlicensed, onChangeOtherUnlicensedName, onChangeOtherUnlicensedValue }) => {
                const otherSelections = userState.otherUnlicensedSelections;
                const enabled = userState.enabledOptions.indexOf(type) > -1;
                const onToggle = () => onToggleOption(type, !enabled);
                const {
                    buttonAddAnotherOther,
                    labelDescription,
                    labelTotalKilolitresPerAnnum,
                    tooltipOther,
                } = messages.page2;
                const onClickAdd = (e: React.FormEvent) => onAddOtherUnlicensed();
            return (
                <React.Fragment>
                    <OtherRow>
                        <ToggleRow>
                            <Switch className="Switch" checked={enabled === true} onChange={onToggle} value="checked" color="primary"/>
                            <ItemLabel className="ItemLabel">{label} {tooltipOther && tooltipOther.length > 0 && <Tooltip text={tooltipOther} position="left"/>}</ItemLabel>
                        </ToggleRow>
                        
                        <LayoutOther>
                            {otherSelections.length > 0 && (
                                <React.Fragment>
                                    <Label>{labelDescription}</Label>
                                    <Label>{labelTotalKilolitresPerAnnum}</Label>
                                </React.Fragment>
                            )}
                            {otherSelections.map((item, index) => {
                                const onChangeName = (value: string) => onChangeOtherUnlicensedName(index, value);
                                const onChangeValue = (value: number) => onChangeOtherUnlicensedValue(index, value);
                                return (
                                    <ControlOther
                                        rowNumber={index + 2}
                                        isParentEnabled={enabled}
                                        key={index}
                                        name={item.name}
                                        value={Number(item.value)}
                                        onChangeName={onChangeName}
                                        onChangeValue={onChangeValue}
                                        placeholder={placeholder || ''}
                                    />
                                );
                            })}
                        </LayoutOther>
                    </OtherRow>
                    <Button disabled={!enabled} onClick={onClickAdd}>{buttonAddAnotherOther}</Button>
                </React.Fragment>
            );
        }}
    </Context.Consumer>
);

const UnlicensedWaterUseStyled = styled.div`
    @media ${devices.tablet} {}
`;

export default () => (
    <Context.Consumer>
        {({ 
            gardenMax,
            gardenStep,
            fireFighting, 
            getTotal,
            messages, 
            userState,
            onChangeHousehold,
            onChangeGarden,
            onChangeFireFightingFirePumpAndHosesValue,
            onChangeFireFightingSprinklerSystemValue,
            onChangeWeedControl,
        }) => {
            const { 
                title, 
                description,
                headingTotal,
                labelHousehold,
                labelGarden,
                labelNumberOfDwellings,
                labelFireFightingPump,
                labelFireFightingSprinkler,
                // labelWeedControl,
                labelStock,
                labelOther,
                labelNumberOfResidents,
                labelHectaresIrrigated,
                // labelHectaresSprayed,
                tooltipHousehold,
                tooltipGarden,
                tooltipFireFightingDwelling,
                // tooltipWeedControl,
                placeholderOtherDescription,
            } = messages.page2;

            const {
                household,
                garden,
                fireFightingFirePumpAndHoses,
                fireFightingSprinklerSystem,
                // weedControl,
            } = userState;

            const total = getTotal(EPage.UnlicensedWaterUse) + ' kL';

            return (
                <UnlicensedWaterUseStyled>
                    <h2>{title}</h2>
                    <Markdown source={description}/>
                    <RowContainer>
                        <GenericRowComponent total={household.total} type={ESourceType.Household} label={labelHousehold} controls={[{ label: labelNumberOfResidents, tooltip: tooltipHousehold, inputType: 'number', value: household.value, onChange: onChangeHousehold }]}/>
                        <GenericRowComponent total={garden.total} type={ESourceType.Garden} label={labelGarden} controls={[{
                            inputType: 'number', 
                            label: labelHectaresIrrigated, 
                            max: gardenMax,
                            onChange: onChangeGarden,
                            step: gardenStep,
                            tooltip: tooltipGarden,
                            value: garden.value,
                        }]}/>
                        <GenericRowComponent
                            total={fireFightingFirePumpAndHoses.total}
                            type={ESourceType.FireFightingFirePumpAndHoses}
                            label={labelFireFightingPump}
                            controls={[
                                { label: labelNumberOfDwellings, tooltip: tooltipFireFightingDwelling, inputType: 'number', value: fireFightingFirePumpAndHoses.value, onChange: onChangeFireFightingFirePumpAndHosesValue },
                            ]}
                        />
                        <GenericRowComponent
                            total={fireFightingSprinklerSystem.total}
                            type={ESourceType.FireFightingSprinklerSystem}
                            label={labelFireFightingSprinkler}
                            controls={[
                                { label: labelNumberOfDwellings, tooltip: tooltipFireFightingDwelling, inputType: 'number', value: fireFightingSprinklerSystem.value, onChange: onChangeFireFightingSprinklerSystemValue },
                            ]}
                        />
                        {/*<GenericRowComponent total={weedControl.total} type={ESourceType.WeedControl} label={labelWeedControl} controls={[{ label: labelHectaresSprayed, tooltip: tooltipWeedControl, inputType: 'number', value: weedControl.value, onChange: onChangeWeedControl }]}/>*/}
                        <StockRowComponent type={ESourceType.Stock} label={labelStock} />
                        <OtherRowComponent type={ESourceType.OtherUnlicensed} label={labelOther} placeholder={placeholderOtherDescription}/>
                    </RowContainer>
                    <hr className="green"/>
                    <TotalFooter label={headingTotal} total={total} />
                </UnlicensedWaterUseStyled>
            )
        }}
    </Context.Consumer>
);


// WEBPACK FOOTER //
// ./src/Components/Pages/UnlicensedWaterUse.tsx